import {
  alpha,
  Button,
  CardContent,
  Stack,
  styled,
  Typography,
  useTheme,
} from '@mui/material';
import { useMap } from '@vis.gl/react-google-maps';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { isEqual } from 'lodash';
import { memo, useCallback } from 'react';
import {
  anchoredPartnerCardId,
  hoveredGroupKey,
  hoveredTeamId,
  isGroupSelectedFamily,
  mapMarkerByKeyFamily,
  selectedGroupKey,
  selectedPartnerMapTeamId,
  teamDetailPanelVisible,
  useIsAnyTeamHovered,
} from '@/common/atoms/community';
import { TrackCommunityEvent } from '$analytics/useTrackAnalytics/domains/community/events';
import { useTrackCommunityAnalytics } from '$analytics/useTrackAnalytics/domains/community/useTrackCommunityAnalytics';
import type { TeamSearchResultVirtualItem } from '../../hooks/useGetPartners/helpers';
import { TeamLogo } from '../TeamLogo/TeamLogo';

const StyledButton = styled(Button)(({ theme }) => ({
  textAlign: 'unset',
  color: theme.palette.text.primary,
  ':hover': {
    backgroundColor: theme.palette.utility.dark,
  },
  '.loading &': {
    opacity: 0.38,
    pointerEvents: 'none',
    cursor: 'default',
  },
}));

type Props = TeamSearchResultVirtualItem & {
  index: number;
};

export const PartnerCard = memo(
  ({
    mailingAddress,
    legalName,
    alias,
    logoUri,
    id,
    groupKey,
    isFirstInGroup,
    isLastInGroup,
    isFocused,
    index,
  }: Props) => {
    const trackEvent = useTrackCommunityAnalytics();

    const [hoveredTeam, setHoveredTeam] = useAtom(hoveredTeamId);
    const theme = useTheme();
    const [hoveredGroup, setHoveredGroup] = useAtom(hoveredGroupKey);
    const setSelectedTeamId = useSetAtom(selectedPartnerMapTeamId);
    const setTeamDetailPanelVisible = useSetAtom(teamDetailPanelVisible);
    const setSelectedGroup = useSetAtom(selectedGroupKey);
    const setAnchoredPartnerId = useSetAtom(anchoredPartnerCardId);
    const mapMarker = useAtomValue(mapMarkerByKeyFamily(groupKey));
    const groupSelected = useAtomValue(isGroupSelectedFamily(groupKey));
    const isPanelVisible = useAtomValue(teamDetailPanelVisible);
    const selectedId = useAtomValue(selectedPartnerMapTeamId);
    const isAnyTeamHovered = useIsAnyTeamHovered();
    const highlightCard = isPanelVisible && selectedId === id;
    const map = useMap();

    const applyHoverStyles =
      !!hoveredTeam || (!!hoveredGroup && !isAnyTeamHovered);

    const handleClick = useCallback(() => {
      setSelectedTeamId(id);
      setTeamDetailPanelVisible(true);
      setSelectedGroup((prevSelectedGroup: string | undefined) => {
        if (groupKey !== prevSelectedGroup) {
          return undefined;
        }
        return prevSelectedGroup;
      });
      setAnchoredPartnerId(undefined);
      trackEvent(TrackCommunityEvent.PartnerMapTeamSelection, {
        eventTarget: 'team_list_item',
        teamId: id,
        teamAddressId: mailingAddress.id,
        inBoundary: isFocused,
        geoValue: [
          mailingAddress.geolocationLatitude,
          mailingAddress.geolocationLongitude,
        ],
        teamOrder: index,
      });

      // Zoom/pan map so selected team's pin is in view
      if (map) {
        const markerInCluster =
          mapMarker && 'map' in mapMarker && mapMarker.map === null;
        const mapBounds = map.getBounds();
        const teamLatLng = {
          lat: mailingAddress.geolocationLatitude,
          lng: mailingAddress.geolocationLongitude,
        };
        const outOfBounds = mapBounds && !mapBounds.contains(teamLatLng);
        if (outOfBounds || markerInCluster) {
          map.setCenter(teamLatLng);
          if (markerInCluster) {
            map.setZoom(17);
          }
        }
      }
    }, [
      id,
      mailingAddress,
      groupKey,
      isFocused,
      index,
      map,
      mapMarker,
      setSelectedTeamId,
      setTeamDetailPanelVisible,
      setSelectedGroup,
      setAnchoredPartnerId,
      trackEvent,
    ]);

    const hoverStyles: {
      backgroundColor?: string;
    } = {
      backgroundColor: undefined,
    };
    if (applyHoverStyles) {
      hoverStyles.backgroundColor = alpha(theme.palette.action.hover, 0.04);
    }
    if (highlightCard) {
      hoverStyles.backgroundColor = alpha(theme.palette.primary.main, 0.08);
    }

    return (
      <StyledButton
        aria-selected={highlightCard}
        fullWidth
        sx={[
          {
            justifyContent: 'flex-start',
            borderBottom: `1px solid ${theme.palette.grey[300]}`,
            px: 2,
            py: 1,
            minHeight: 135,
          },
          hoverStyles,
          groupSelected && {
            borderLeft: `1px solid ${theme.palette.grey[400]}`,
            borderRight: `1px solid ${theme.palette.grey[400]}`,
            borderTop: isFirstInGroup
              ? `1px solid ${theme.palette.grey[400]}`
              : undefined,
            borderBottom: isLastInGroup
              ? `1px solid ${theme.palette.grey[400]}`
              : undefined,
          },
        ]}
        onClick={handleClick}
        onMouseEnter={() => {
          setHoveredTeam(id);
          setHoveredGroup(groupKey);
        }}
        onMouseLeave={() => {
          setHoveredTeam(undefined);
          setHoveredGroup(undefined);
        }}
      >
        <TeamLogo
          logoUri={logoUri}
          legalName={legalName ?? undefined}
          sx={{
            width: 75,
            height: logoUri ? 75 : undefined,
            objectFit: 'contain',
            flexShrink: 0,
          }}
        />
        <CardContent component={Stack} sx={{ gap: 1 }}>
          <Typography variant="h4" component="span">
            {legalName?.length ? legalName : alias}
          </Typography>
          {alias && legalName && (
            <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
              {alias}
            </Typography>
          )}
          <Typography variant="body2">
            {mailingAddress.formattedAddress}
          </Typography>
        </CardContent>
      </StyledButton>
    );
  },
  (prevProps, newProps) => isEqual(prevProps, newProps),
);

PartnerCard.displayName = 'PartnerCard';
